import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";

export const MOBILE_QUERY = "(max-width: 767px)";

export const isMobileScreen = () =>
  typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;

export const INFINITE_LOADING_ATTR = "data-infinite-loading";
const PENDING_TIMEOUT = 15000;

const sameItems = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const setBodyLoading = (on) => {
  if (on) document.body.setAttribute(INFINITE_LOADING_ATTR, "");
  else document.body.removeAttribute(INFINITE_LOADING_ATTR);
};

const useInfiniteScroll = ({
  data,
  page,
  pageCount,
  onLoadMore,
  resetToFirstPage = true,
}) => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [rows, setRows] = useState(data || []);
  const [loadedPage, setLoadedPage] = useState(page || 1);
  const [pendingPage, setPendingPage] = useState(null);
  const [sentinel, setSentinel] = useState(null);
  const pendingRef = useRef(null);
  const loadedPageRef = useRef(page || 1);
  // Rows of the most recently loaded page, to recognise a refetch of that same page.
  const lastChunkRef = useRef(data || []);
  const pageRef = useRef(page);
  const isMobileRef = useRef(isMobile);
  const onLoadMoreRef = useRef(onLoadMore);
  const resetRef = useRef(resetToFirstPage);
  pageRef.current = page;
  resetRef.current = resetToFirstPage;
  isMobileRef.current = isMobile;
  onLoadMoreRef.current = onLoadMore;

  const setPending = useCallback((value) => {
    pendingRef.current = value;
    setPendingPage(value);
    setBodyLoading(Boolean(value));
  }, []);

  useEffect(() => {
    const items = data || [];
    const current = pageRef.current || 1;
    // Some pages clear their rows before the new ones arrive; keep waiting.
    if (
      items.length === 0 &&
      (pendingRef.current || (current > 1 && current === loadedPageRef.current))
    ) {
      return;
    }
    if (pendingRef.current && current === pendingRef.current) {
      setRows((prev) => [...prev, ...items]);
    } else if (
      current > 1 &&
      current === loadedPageRef.current &&
      sameItems(lastChunkRef.current, items)
    ) {
      setPending(null);
      return;
    } else {
      setRows(items);
      if (
        resetRef.current &&
        isMobileRef.current &&
        current > 1 &&
        !pendingRef.current
      ) {
        onLoadMoreRef.current(1);
      }
    }
    loadedPageRef.current = current;
    lastChunkRef.current = items;
    setLoadedPage(current);
    setPending(null);
  }, [data, setPending]);

  useEffect(() => {
    if (!pendingPage) return;
    const timer = setTimeout(() => setPending(null), PENDING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [pendingPage, setPending]);

  useEffect(() => () => setBodyLoading(false), []);

  const hasMore = loadedPage < (pageCount || 0);

  useEffect(() => {
    if (!isMobile || !sentinel || !hasMore || pendingPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !pendingRef.current) {
          const next = loadedPage + 1;
          setPending(next);
          onLoadMoreRef.current(next);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isMobile, sentinel, hasMore, pendingPage, loadedPage, setPending]);

  return {
    rows: isMobile ? rows : data || [],
    isMobile,
    isLoadingMore: Boolean(pendingPage),
    sentinelRef: setSentinel,
  };
};

export default useInfiniteScroll;
